import React, { useEffect, useMemo, useState } from 'react';

export interface WindowSize {
  width: number;
  height: number;
}

export interface ScreenSizeContextValue {
  windowSize: WindowSize;
  matchedBreakpoints: string[];
}

const getWindowSize = () => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 0,
  height: typeof window !== 'undefined' ? window.innerHeight : 0
});

export const ScreenSizeContext = React.createContext<ScreenSizeContextValue>({
  windowSize: getWindowSize(),
  matchedBreakpoints: []
});

export const breakpoints = {
  xxs: 320,
  xs: 525,
  sm: 725,
  md: 930,
  lg: 1080,
  xl: 1232,
  xxl: 1440,
  xxxl: 1600
};

const ScreenSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [windowSize, setWindowSize] = useState<WindowSize>({
    width: 0,
    height: 0
  });
  const matchedBreakpoints = useMemo(() => {
    const { width } = windowSize;
    const matched: string[] = [];

    Object.entries(breakpoints).forEach(([breakpoint, size]) => {
      if (width >= size) {
        matched.push(breakpoint);
      }
    });

    return matched;
  }, [windowSize]);

  const handleResize = () => {
    setWindowSize(getWindowSize());
  };

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <ScreenSizeContext.Provider value={{ windowSize, matchedBreakpoints }}>
      {children}
    </ScreenSizeContext.Provider>
  );
};

export default ScreenSizeProvider;
